import type {
  Delivery,
  WebHookId,
} from 'pages/Settings/Integration/WebHooks/WebHooksState';
import type { CreateOrUpdateDelivery } from 'pages/Settings/Integration/WebHooks/commands/createOrUpdateDelivery';
import type { DeleteDeliveriesCommand } from 'pages/Settings/Integration/WebHooks/commands/deleteDeliveries';
import {
  bandwidthMsgReceived,
  netWorkerLogsReceived,
  newDeviceSaved,
  serverTimestampReceived,
  snapshotInfoReceived,
  snapshotStatusReceived,
  taskerLogsReceived,
  workerLastCommunication,
  workerLogsReceived,
} from 'pages/SnapshotManagement/actions';
import io from 'socket.io-client';

type ReduxDispatch = (args: { type: string; payload: any }) => void;
// SOCKET_URL is processed during the frontend build
declare const SOCKET_URL: string;

export const url =
  SOCKET_URL.length === 0
    ? `//${window.location.hostname}:${window.location.port}/`
    : SOCKET_URL;
export const SOCKET_CONNECTION_DELAY = 3000;
export const rooms = {
  colorFilter: 'colorFilter',
  snapshot: 'snapshot',
  system: 'system',
  systemStatus: 'systemStatus',
};

const socket = io(url, {
  reconnectionDelayMax: SOCKET_CONNECTION_DELAY,
  autoConnect: false,
});

export const ipfSocket = {
  socket,
  socketIsConnected: false,
};

export const leaveRoom = (roomName: string): void => {
  ipfSocket.socket.emit('leave', roomName);
};

export const joinRoom = (roomName: string, ack?: () => void): void => {
  ipfSocket.socket.emit('join', roomName, ack);
};

export const authenticate = (): void => {
  ipfSocket.socket.emit('authenticate');
};

export const connectSocket = (): void => {
  if (!ipfSocket.socketIsConnected) {
    ipfSocket.socket.connect();
  }
};

export const disconnectSocket = (): void => {
  ipfSocket.socket.disconnect();
};

export const reconnectSocket = (): void => {
  ipfSocket.socket.close();
  ipfSocket.socket.open();
};

export const isSocketConnected = (): boolean => ipfSocket.socket.connected;

export const connectRunningSnapshot = (dispatch: ReduxDispatch): void => {
  ipfSocket.socket.on('snapshot:infoStatus', (message: string) =>
    dispatch(snapshotInfoReceived(message)),
  );
  ipfSocket.socket.on('snapshot:updated', (message: string) =>
    dispatch(snapshotStatusReceived(message)),
  );
  ipfSocket.socket.on('system:serverTimestamp', (message: string) =>
    dispatch(serverTimestampReceived(message)),
  );
};

export const connectRunningSnapshotBandwidth = (
  dispatch: ReduxDispatch,
): void => {
  ipfSocket.socket.on('system:actualBandwidth', (message: string) =>
    dispatch(bandwidthMsgReceived(message)),
  );
};

export const connectRunningSnapshotNewDevice = (
  dispatch: ReduxDispatch,
): void => {
  ipfSocket.socket.on('snapshot:newDeviceSaved', (device: string) =>
    dispatch(newDeviceSaved(device)),
  );
  ipfSocket.socket.off('snapshot:logs:tasker');
  ipfSocket.socket.off('snapshot:logs:worker');
  ipfSocket.socket.off('snapshot:logs:netWorker');
  ipfSocket.socket.off('snapshot:lastCommunication:info');
};

export const connectRunningSnapshotInProgress = (
  dispatch: ReduxDispatch,
): void => {
  ipfSocket.socket.on('snapshot:lastCommunication:info', (payload: unknown) =>
    dispatch(workerLastCommunication(payload)),
  );
  ipfSocket.socket.off('snapshot:newDeviceSaved');
  ipfSocket.socket.off('snapshot:logs:tasker');
  ipfSocket.socket.off('snapshot:logs:worker');
  ipfSocket.socket.off('snapshot:logs:netWorker');
};

export const connectRunningSnapshotServiceLogs = (
  dispatch: ReduxDispatch,
): void => {
  ipfSocket.socket.on('snapshot:logs:tasker', (payload: string[]) =>
    dispatch(taskerLogsReceived(payload)),
  );
  ipfSocket.socket.on('snapshot:logs:worker', (payload: string[]) =>
    dispatch(workerLogsReceived(payload)),
  );
  ipfSocket.socket.on('snapshot:logs:netWorker', (payload: string[]) =>
    dispatch(netWorkerLogsReceived(payload)),
  );
  ipfSocket.socket.off('snapshot:newDeviceSaved');
  ipfSocket.socket.off('snapshot:lastCommunication:info');
};

export const disconnectRunningSnapshot = (): void => {
  ipfSocket.socket.off('snapshot:lastCommunication:info');
  ipfSocket.socket.off('snapshot:logs:tasker');
  ipfSocket.socket.off('snapshot:logs:worker');
  ipfSocket.socket.off('snapshot:logs:netWorker');
  ipfSocket.socket.off('snapshot:infoStatus');
  ipfSocket.socket.off('snapshot:newDeviceSaved');
  ipfSocket.socket.off('snapshot:updated');
  ipfSocket.socket.off('system:actualBandwidth');
  ipfSocket.socket.off('system:serverTimestamp');
};

export const connectWebhookDeliveryHistory = (
  createOrUpdateDelivery: (command: CreateOrUpdateDelivery) => void,
  deleteDeliveries: (command: DeleteDeliveriesCommand) => void,
  webhookId: WebHookId,
): void => {
  ipfSocket.socket.on(
    'webhookDeliveryHistory:created',
    (deliveries: Delivery[]) =>
      createOrUpdateDelivery({ deliveries, webhookId }),
  );
  ipfSocket.socket.on(
    'webhookDeliveryHistory:updated',
    (deliveries: Delivery[]) =>
      createOrUpdateDelivery({ deliveries, webhookId }),
  );
  ipfSocket.socket.on(
    'webhookDeliveryHistory:removed',
    (deliveryIdsToDelete: string[]) =>
      deleteDeliveries({ deliveryIdsToDelete }),
  );
};

export const disconnectWebhookDeliveryHistory = (): void => {
  ipfSocket.socket.off('webhookDeliveryHistory:created');
  ipfSocket.socket.off('webhookDeliveryHistory:updated');
  ipfSocket.socket.off('webhookDeliveryHistory:removed');
};

export const socketConnection = (
  socketConnected: () => void,
  socketDisconnected: (reason: string) => void,
  socketConnectionError: () => void,
): void => {
  ipfSocket.socket.on('connect', () => socketConnected());
  ipfSocket.socket.on('reconnect', () => socketConnected());
  ipfSocket.socket.on('disconnect', (reason: string) =>
    socketDisconnected(reason),
  );
  ipfSocket.socket.on('connect_error', () => socketConnectionError());
  ipfSocket.socket.on('connect_timeout', () => socketConnectionError());
  ipfSocket.socket.on('error', () => socketConnectionError());
  ipfSocket.socket.on('reconnect_error', () => socketConnectionError());
  ipfSocket.socket.on('reconnect_failed', () => socketConnectionError());
};
